import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  Chip,
  Paper
} from '@mui/material';
import {
  AutoAwesome,
  IntegrationInstructions,
  Schedule,
  Analytics,
  Speed,
  ViewModule
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';

// Dark theme colors
const colors = {
  bgPrimary: '#0a0a0a',
  bgSecondary: '#141414',
  bgSurface: '#1a1a1a',
  border: '#2a2a2a',
  textPrimary: '#e5e5e5',
  textSecondary: '#a3a3a3',
  textMuted: '#737373',
  accentPrimary: '#3b82f6',
  accentSecondary: '#8b5cf6',
  success: '#22c55e'
};

const MarketingHome = () => {
  const navigate = useNavigate();
  const { isAuthenticated, loading } = useAuth();

  useEffect(() => {
    if (!loading && isAuthenticated) {
      navigate('/dashboard', { replace: true });
    }
  }, [isAuthenticated, loading, navigate]);

  if (loading || isAuthenticated) {
    return null;
  }

  const features = [
    {
      icon: <ViewModule />,
      title: 'Shape the Work',
      description: 'Write pitches that frame the problem, sketch a solution, and set a clear appetite before anyone commits to building.',
      color: colors.accentPrimary
    },
    {
      icon: <AutoAwesome />,
      title: 'Betting Table',
      description: 'Bet on the pitches worth doing this cycle. If it isn\'t bet on, it doesn\'t get built — no endless backlog to groom.',
      color: colors.accentSecondary
    },
    {
      icon: <Schedule />,
      title: 'Six-Week Cycles',
      description: 'Give a small team a fixed time budget and full autonomy to build meaningful work end-to-end, then cool down.',
      color: colors.success
    },
    {
      icon: <Analytics />,
      title: 'Hill Chart Tracking',
      description: 'See real progress as scopes move from figuring things out (uphill) to getting them done (downhill) — no status meetings.',
      color: '#ec4899'
    },
    {
      icon: <Speed />,
      title: 'Fixed Time, Variable Scope',
      description: 'Decide how much an idea is worth, then shape the scope to fit the appetite instead of estimating tasks endlessly.',
      color: '#f59e0b'
    },
    {
      icon: <IntegrationInstructions />,
      title: 'Jira Integration',
      description: 'Import work from Jira, link bets and scopes to issues, and keep everything in sync without leaving the app.',
      color: '#0ea5e9'
    }
  ];

  const researchQuotes = [
    {
      quote: "Fixed time, variable scope. We make the deadline immovable and shape the scope to fit — so projects ship instead of dragging on.",
      citation: "Shape Up, Basecamp"
    },
    {
      quote: "A bet is a commitment: one small team, one project, one cycle — with uninterrupted time and full responsibility for the outcome.",
      citation: "The Shape Up Method"
    },
    {
      quote: "The hill chart shows what's been figured out and what's still unknown, so status is something you can see — not something you guess.",
      citation: "Shape Up Principles"
    }
  ];

  return (
    <Box sx={{ bgcolor: colors.bgPrimary, minHeight: '100vh' }}>
      {/* Hero Section */}
      <Box
        sx={{
          background: `linear-gradient(to bottom, ${colors.bgPrimary} 0%, ${colors.bgSecondary} 100%)`,
          py: { xs: 8, md: 12 },
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundImage: `radial-gradient(circle at 1px 1px, ${colors.border} 1px, transparent 0)`,
            backgroundSize: '32px 32px',
            opacity: 0.5
          },
          '&::after': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: `radial-gradient(ellipse at top, rgba(59, 130, 246, 0.08) 0%, transparent 50%),
                        radial-gradient(ellipse at bottom right, rgba(139, 92, 246, 0.05) 0%, transparent 50%)`,
            pointerEvents: 'none'
          }
        }}
      >
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <Grid container spacing={6} alignItems="center">
            <Grid item xs={12} md={6}>
              <Chip
                label="Shape Up for Product Teams"
                sx={{
                  mb: 3,
                  bgcolor: colors.bgSurface,
                  color: colors.textSecondary,
                  border: `1px solid ${colors.border}`,
                  fontWeight: 500,
                  '& .MuiChip-label': { px: 2 }
                }}
              />

              <Typography
                variant="h1"
                component="h1"
                sx={{
                  fontSize: { xs: '2.5rem', md: '3.5rem', lg: '4rem' },
                  fontWeight: 700,
                  mb: 3,
                  color: colors.textPrimary,
                  lineHeight: 1.1
                }}
              >
                Shape the Work.{' '}
                <Box
                  component="span"
                  sx={{
                    background: `linear-gradient(135deg, ${colors.accentPrimary} 0%, ${colors.accentSecondary} 100%)`,
                    backgroundClip: 'text',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent'
                  }}
                >
                  Bet on What Matters.
                </Box>
              </Typography>

              <Typography
                variant="h5"
                sx={{
                  mb: 4,
                  lineHeight: 1.6,
                  fontWeight: 400,
                  color: colors.textSecondary,
                  fontSize: { xs: '1.1rem', md: '1.25rem' }
                }}
              >
                Plan pitches, place bets, and track progress on hill charts. Give your teams
                a fixed appetite and the autonomy to ship — without endless backlogs or status meetings.
              </Typography>

              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 4 }}>
                <Button
                  variant="contained"
                  size="large"
                  onClick={() => navigate('/register')}
                  sx={{
                    px: 4,
                    py: 1.5,
                    fontSize: '1rem',
                    background: `linear-gradient(135deg, ${colors.accentPrimary} 0%, ${colors.accentSecondary} 100%)`,
                    boxShadow: `0 0 20px rgba(59, 130, 246, 0.15)`,
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: `0 0 30px rgba(139, 92, 246, 0.2)`
                    }
                  }}
                >
                  Start Free
                </Button>
                <Button
                  variant="outlined"
                  size="large"
                  onClick={() => navigate('/features')}
                  sx={{
                    px: 4,
                    py: 1.5,
                    fontSize: '1rem',
                    borderColor: colors.border,
                    color: colors.textPrimary,
                    '&:hover': {
                      borderColor: colors.accentPrimary,
                      bgcolor: colors.bgSurface
                    }
                  }}
                >
                  View Features
                </Button>
              </Box>
            </Grid>

            <Grid item xs={12} md={6}>
              {/* Terminal Demo */}
              <Paper
                elevation={0}
                sx={{
                  bgcolor: colors.bgSecondary,
                  border: `1px solid ${colors.border}`,
                  borderRadius: 2,
                  overflow: 'hidden',
                  boxShadow: `0 25px 50px -12px rgba(0, 0, 0, 0.6), 0 0 20px rgba(59, 130, 246, 0.15)`
                }}
              >
                {/* Terminal Header */}
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    p: 2,
                    bgcolor: colors.bgSurface,
                    borderBottom: `1px solid ${colors.border}`
                  }}
                >
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: '#ff5f57' }} />
                    <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: '#ffbd2e' }} />
                    <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: '#28ca42' }} />
                  </Box>
                  <Typography
                    variant="caption"
                    sx={{
                      color: colors.textMuted,
                      fontFamily: 'JetBrains Mono, monospace',
                      flex: 1,
                      textAlign: 'center'
                    }}
                  >
                    shape-up-cycle
                  </Typography>
                </Box>

                {/* Terminal Content */}
                <Box sx={{ p: 3, fontFamily: 'JetBrains Mono, monospace', fontSize: '0.875rem', lineHeight: 1.8 }}>
                  <Box sx={{ mb: 1 }}>
                    <Typography component="span" sx={{ color: colors.success, fontFamily: 'inherit' }}>$</Typography>
                    <Typography component="span" sx={{ color: colors.textPrimary, fontFamily: 'inherit' }}> shapeup cycle start --team product</Typography>
                  </Box>
                  <Typography sx={{ color: colors.textSecondary, fontFamily: 'inherit', mb: 0.5 }}>Opening betting table...</Typography>
                  <Typography sx={{ color: colors.textSecondary, fontFamily: 'inherit', mb: 0.5 }}>Reviewing 3 shaped pitches</Typography>
                  <Box sx={{ mb: 1 }}>
                    <Typography component="span" sx={{ color: colors.accentPrimary, fontFamily: 'inherit' }}>Cycle ready: </Typography>
                    <Typography component="span" sx={{ color: colors.accentSecondary, fontFamily: 'inherit' }}>cycle/2026-c3</Typography>
                  </Box>
                  <Box sx={{ height: 16 }} />
                  <Box sx={{ mb: 0.5 }}>
                    <Typography component="span" sx={{ color: colors.textSecondary, fontFamily: 'inherit' }}>Bet: </Typography>
                    <Typography component="span" sx={{ color: colors.textPrimary, fontFamily: 'inherit' }}>BET-142</Typography>
                    <Typography component="span" sx={{ color: colors.textSecondary, fontFamily: 'inherit' }}> Streamlined Onboarding</Typography>
                  </Box>
                  <Box sx={{ mb: 1 }}>
                    <Typography component="span" sx={{ color: colors.textSecondary, fontFamily: 'inherit' }}>Appetite: </Typography>
                    <Typography component="span" sx={{ color: colors.accentPrimary, fontFamily: 'inherit' }}>6 weeks</Typography>
                    <Typography component="span" sx={{ color: colors.textSecondary, fontFamily: 'inherit' }}> (1 small team)</Typography>
                  </Box>
                  <Box sx={{ height: 16 }} />
                  <Box sx={{ mb: 0.5 }}>
                    <Typography component="span" sx={{ color: colors.textSecondary, fontFamily: 'inherit' }}>Hill: </Typography>
                    <Typography component="span" sx={{ color: colors.accentSecondary, fontFamily: 'inherit' }}>Scoping </Typography>
                    <Typography component="span" sx={{ color: colors.textMuted, fontFamily: 'inherit' }}>→ </Typography>
                    <Typography component="span" sx={{ color: colors.accentPrimary, fontFamily: 'inherit' }}>Building </Typography>
                    <Typography component="span" sx={{ color: colors.textMuted, fontFamily: 'inherit' }}>→ </Typography>
                    <Typography component="span" sx={{ color: colors.textSecondary, fontFamily: 'inherit' }}>Done</Typography>
                  </Box>
                  <Box>
                    <Typography component="span" sx={{ color: colors.textSecondary, fontFamily: 'inherit' }}>Status: </Typography>
                    <Typography component="span" sx={{ color: colors.success, fontFamily: 'inherit' }}>downhill · on track</Typography>
                  </Box>
                </Box>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Features Section */}
      <Box sx={{ bgcolor: colors.bgSecondary, py: { xs: 8, md: 12 } }}>
        <Container maxWidth="lg">
          <Box textAlign="center" mb={8}>
            <Typography
              variant="h2"
              component="h2"
              gutterBottom
              fontWeight="bold"
              sx={{ color: colors.textPrimary }}
            >
              Built for how teams actually ship
            </Typography>
            <Typography
              variant="h5"
              maxWidth="600px"
              mx="auto"
              sx={{ color: colors.textSecondary }}
            >
              Stop grooming backlogs. Give teams a clear bet and the room to build it.
            </Typography>
          </Box>

          <Grid container spacing={4}>
            {features.map((feature, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <Card
                  sx={{
                    height: '100%',
                    transition: 'all 0.3s ease',
                    bgcolor: colors.bgSurface,
                    border: `1px solid ${colors.border}`,
                    position: 'relative',
                    overflow: 'hidden',
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      height: 2,
                      background: `linear-gradient(90deg, ${colors.accentPrimary}, ${colors.accentSecondary})`,
                      transform: 'scaleX(0)',
                      transition: 'transform 0.3s ease'
                    },
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: `0 20px 40px rgba(0,0,0,0.3), 0 0 20px rgba(59, 130, 246, 0.15)`,
                      borderColor: 'rgba(59, 130, 246, 0.3)',
                      '&::before': {
                        transform: 'scaleX(1)'
                      }
                    }
                  }}
                >
                  <CardContent sx={{ p: 4 }}>
                    <Box
                      sx={{
                        width: 48,
                        height: 48,
                        borderRadius: 2,
                        background: `linear-gradient(135deg, ${feature.color}20, ${feature.color}40)`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mb: 3,
                        color: feature.color
                      }}
                    >
                      {feature.icon}
                    </Box>

                    <Typography
                      variant="h6"
                      component="h3"
                      gutterBottom
                      fontWeight="bold"
                      sx={{ color: colors.textPrimary }}
                    >
                      {feature.title}
                    </Typography>

                    <Typography
                      variant="body1"
                      lineHeight={1.6}
                      sx={{ color: colors.textSecondary }}
                    >
                      {feature.description}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* How It Works Section */}
      <Box sx={{ bgcolor: colors.bgPrimary, py: { xs: 8, md: 12 } }}>
        <Container maxWidth="lg">
          <Box textAlign="center" mb={8}>
            <Typography
              variant="h2"
              component="h2"
              gutterBottom
              fontWeight="bold"
              sx={{ color: colors.textPrimary }}
            >
              How Shape Up works
            </Typography>
            <Typography
              variant="h5"
              sx={{ color: colors.textSecondary }}
            >
              Fixed time, variable scope — a proven way to ship meaningful work in six weeks.
            </Typography>
          </Box>

          <Grid container spacing={4}>
            {[
              { num: 1, title: 'Shape the work', desc: 'Set an appetite and shape a pitch that defines the problem, solution, and boundaries.' },
              { num: 2, title: 'Place your bets', desc: 'At the betting table, decide which pitches are worth doing this cycle.' },
              { num: 3, title: 'Build in a cycle', desc: 'A small team takes full responsibility and builds the bet end-to-end.' },
              { num: 4, title: 'Track on the hill', desc: 'Watch scopes move from uphill (unknowns) to downhill (done) on the hill chart.' }
            ].map((step, index) => (
              <Grid item xs={12} sm={6} md={3} key={index}>
                <Box sx={{ textAlign: 'center', p: 3 }}>
                  <Box
                    sx={{
                      width: 48,
                      height: 48,
                      borderRadius: '50%',
                      background: `linear-gradient(135deg, ${colors.accentPrimary}, ${colors.accentSecondary})`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mx: 'auto',
                      mb: 3,
                      color: 'white',
                      fontWeight: 700,
                      fontSize: '1.25rem'
                    }}
                  >
                    {step.num}
                  </Box>
                  <Typography
                    variant="h6"
                    gutterBottom
                    fontWeight="bold"
                    sx={{ color: colors.textPrimary }}
                  >
                    {step.title}
                  </Typography>
                  <Typography sx={{ color: colors.textSecondary }}>
                    {step.desc}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Research Section */}
      <Box
        sx={{
          bgcolor: colors.bgSecondary,
          py: { xs: 8, md: 12 },
          borderTop: `1px solid ${colors.border}`,
          borderBottom: `1px solid ${colors.border}`
        }}
      >
        <Container maxWidth="lg">
          <Box textAlign="center" mb={8}>
            <Typography
              variant="h2"
              component="h2"
              gutterBottom
              fontWeight="bold"
              sx={{ color: colors.textPrimary }}
            >
              Why Shape Up works
            </Typography>
            <Typography
              variant="h5"
              sx={{ color: colors.textSecondary }}
            >
              A proven approach from Basecamp for shipping meaningful work without endless backlogs.
            </Typography>
          </Box>

          <Grid container spacing={4}>
            {researchQuotes.map((item, index) => (
              <Grid item xs={12} md={4} key={index}>
                <Card
                  sx={{
                    height: '100%',
                    bgcolor: colors.bgSurface,
                    border: `1px solid ${colors.border}`,
                    p: 3
                  }}
                >
                  <Typography
                    variant="body1"
                    sx={{
                      fontStyle: 'italic',
                      color: colors.textSecondary,
                      mb: 2,
                      lineHeight: 1.6
                    }}
                  >
                    "{item.quote}"
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: colors.textMuted,
                      fontFamily: 'JetBrains Mono, monospace'
                    }}
                  >
                    - {item.citation}
                  </Typography>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* CTA Section */}
      <Box
        sx={{
          bgcolor: colors.bgSurface,
          py: { xs: 8, md: 12 },
          textAlign: 'center',
          position: 'relative',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: `radial-gradient(ellipse at center, rgba(59, 130, 246, 0.1) 0%, transparent 70%)`,
            pointerEvents: 'none'
          }
        }}
      >
        <Container maxWidth="md" sx={{ position: 'relative', zIndex: 1 }}>
          <Typography
            variant="h2"
            component="h2"
            gutterBottom
            fontWeight="bold"
            sx={{ color: colors.textPrimary }}
          >
            Ready to ship work that matters?
          </Typography>

          <Typography
            variant="h5"
            sx={{ mb: 4, color: colors.textSecondary }}
          >
            Shape your first pitch and place your first bet in minutes.
            Free to start, no credit card required.
          </Typography>

          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap', mb: 3 }}>
            <Button
              variant="contained"
              size="large"
              onClick={() => navigate('/register')}
              sx={{
                px: 4,
                py: 1.5,
                fontSize: '1rem',
                background: `linear-gradient(135deg, ${colors.accentPrimary} 0%, ${colors.accentSecondary} 100%)`,
                boxShadow: `0 0 20px rgba(59, 130, 246, 0.15)`,
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: `0 0 30px rgba(139, 92, 246, 0.2)`
                }
              }}
            >
              Get Started Free
            </Button>
            <Button
              variant="outlined"
              size="large"
              onClick={() => navigate('/pricing')}
              sx={{
                px: 4,
                py: 1.5,
                fontSize: '1rem',
                borderColor: colors.border,
                color: colors.textPrimary,
                '&:hover': {
                  borderColor: colors.accentPrimary,
                  bgcolor: 'rgba(59, 130, 246, 0.1)'
                }
              }}
            >
              View Pricing
            </Button>
          </Box>

          <Typography variant="body2" sx={{ color: colors.textMuted }}>
            Free plan includes unlimited pitches and bets for up to 5 team members
          </Typography>
        </Container>
      </Box>
    </Box>
  );
};

export default MarketingHome;
